import React, { createContext, useContext, useState, useEffect, useRef, ReactNode, useMemo } from 'react';
import { useAuth } from './AuthContext';
import { Client } from '../types';

interface ClientContextType {
  selectedClientId: number | null;
  setSelectedClientId: (clientId: number | null) => void;
  activeClients: Client[];
  hasMultipleClients: boolean;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAdmin } = useAuth();
  const [selectedClientId, setSelectedClientIdState] = useState<number | null>(null);
  const initializedRef = useRef(false);
  const lastUserIdRef = useRef<number | undefined>(undefined);
  const lastClientsKeyRef = useRef<string>('');

  // Check if user is admin (stable value)
  const isAdminUser = useMemo(() => isAdmin(), [user?.role]);

  // Create stable key for clients to detect changes (before activeClients)
  const clientsKey = useMemo(() => {
    if (!user?.clients) return '';
    return user.clients.map(c => `${c.id}-${c.status}`).join(',');
  }, [user?.clients]);

  // Get active clients (only for client users) - use stable key for dependency
  const activeClients = useMemo(() => {
    if (isAdminUser || !user?.clients) {
      return [];
    }
    return user.clients.filter(c => c.status !== false);
  }, [clientsKey, isAdminUser]); // Use stable string key instead of array reference

  const hasMultipleClients = activeClients.length > 1;

  // Reset when user changes
  useEffect(() => {
    if (user?.id !== lastUserIdRef.current) {
      lastUserIdRef.current = user?.id;
      initializedRef.current = false;
      lastClientsKeyRef.current = '';
      setSelectedClientIdState(null);
    }
  }, [user?.id]);

  // Initialize selectedClientId when clients are available
  useEffect(() => {
    // Only initialize for client users (not admins)
    if (isAdminUser || !user?.clients || activeClients.length === 0) {
      if (activeClients.length === 0 && selectedClientId !== null) {
        setSelectedClientIdState(null);
      }
      return;
    }

    // Only initialize once or when clients actually change
    if (!initializedRef.current || clientsKey !== lastClientsKeyRef.current) {
      lastClientsKeyRef.current = clientsKey;
      initializedRef.current = true;

      // If only 1 client, auto-select it
      if (activeClients.length === 1) {
        const clientId = activeClients[0].id;
        setSelectedClientIdState(clientId);
        localStorage.setItem('selectedClientId', clientId.toString());
        return;
      }

      // For multiple clients, check localStorage or default to first
      const storedClientId = localStorage.getItem('selectedClientId');
      if (storedClientId) {
        const parsedId = parseInt(storedClientId, 10);
        if (activeClients.some(c => c.id === parsedId)) {
          setSelectedClientIdState(parsedId);
          return;
        }
      }

      // Default to first client
      const firstClientId = activeClients[0]?.id || null;
      setSelectedClientIdState(firstClientId);
      if (firstClientId) {
        localStorage.setItem('selectedClientId', firstClientId.toString());
      }
    }
  }, [clientsKey, activeClients.length, isAdminUser, user?.clients]);

  // Sync selectedClientId to localStorage when it changes (only for client users with multiple clients)
  useEffect(() => {
    if (!isAdminUser && hasMultipleClients && selectedClientId !== null) {
      const currentStored = localStorage.getItem('selectedClientId');
      if (currentStored !== selectedClientId.toString()) {
        localStorage.setItem('selectedClientId', selectedClientId.toString());
      }
    }
  }, [selectedClientId, hasMultipleClients, isAdminUser]);

  // Wrapper function to update selectedClientId
  const setSelectedClientId = (clientId: number | null) => {
    setSelectedClientIdState(clientId);
    if (clientId !== null) {
      localStorage.setItem('selectedClientId', clientId.toString());
    } else {
      localStorage.removeItem('selectedClientId');
    }
  };

  return (
    <ClientContext.Provider
      value={{
        selectedClientId,
        setSelectedClientId,
        activeClients,
        hasMultipleClients,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = useContext(ClientContext);
  if (context === undefined) {
    throw new Error('useClient must be used within a ClientProvider');
  }
  return context;
};
